import { auth, db } from "./firebase";
import { doc, getDoc, getDocs, collection, setDoc, query, where } from "firebase/firestore";
import { getActiveSchoolYear } from "./sisAcademicService";
import { loadSectionAdvisers } from "./sectionsService";

function norm(s) {
  return String(s || "").trim().toLowerCase();
}
function emailKey(email) {
  return norm(email).replace(/[^a-z0-9]+/g, "_");
}

function normalizeAdvisories(input) {
  const arr = Array.isArray(input) ? input : [];
  return arr
    .map(a => ({ grade: String(a?.grade || a?.gradeLevel || "").trim(), section: String(a?.section || "").trim() }))
    .filter(a => a.grade && a.section);
}

function normalizeClasses(input) {
  let arr = [];
  if (Array.isArray(input)) arr = input;
  else if (input && typeof input === "object") arr = Object.values(input);
  return arr
    .map(c => ({
      grade: String(c?.grade || c?.gradeLevel || c?.level || "").trim(),
      section: String(c?.section || c?.sec || "").trim(),
      subject: String(c?.subject || c?.name || c?.course || "").trim(),
      schedule: String(c?.schedule || c?.time || "").trim(),
      room: String(c?.room || "").trim(),
      gradebookUrl: String(c?.gradebookUrl || c?.gradebookLink || c?.link || "").trim()
    }))
    .filter(c => c.grade || c.section || c.subject || c.gradebookUrl);
}

export function teacherWorkloadDocId({ uid, email, campus, schoolYear }) {
  const key = uid || norm(email).replace(/[^a-z0-9]+/g, "_");
  const c = norm(campus) || "gapan";
  const sy = String(schoolYear || "").trim() || "unknown";
  return `${key}__${c}__${sy}`;
}

export async function loadTeacherWorkload({ user, campus, schoolYear }) {
  const c = norm(campus || user?.campus) || "gapan";
  const authUid = String(auth.currentUser?.uid || "");
  const authEmail = String(auth.currentUser?.email || "");
  const resolvedUid = authUid || String(user?.uid || "");
  const resolvedEmail = authEmail || String(user?.email || "");
  const metaBase = {
    campus: c,
    uid: resolvedUid,
    email: resolvedEmail,
    attempted: {},
    errors: []
  };
  if (!authUid) {
    metaBase.errors.push({ step: "auth", code: "unauthenticated", message: "Please sign in again." });
    const sy = String(schoolYear || "").trim();
    return {
      id: teacherWorkloadDocId({ uid: resolvedUid, email: resolvedEmail, campus: c, schoolYear: sy || "unknown" }),
      campus: c,
      schoolYear: sy,
      data: {
        uid: resolvedUid,
        email: resolvedEmail,
        campus: c,
        schoolYear: sy,
        advisories: [],
        classes: [],
        _meta: { source: "fallbackAdviserMap", ...metaBase }
      }
    };
  }
  try {
    const uid = resolvedUid || String(user?.uid || "");
    const email = resolvedEmail || String(user?.email || "");
    const candidateIds = [];
    if (uid) candidateIds.push(`${uid}__${c}`);
    if (email) candidateIds.push(`${emailKey(email)}__${c}`);
    metaBase.attempted.currentIds = candidateIds;
    for (const currentId of candidateIds) {
      try {
        const currentRef = doc(db, "teacher_workloads_current", currentId);
        const currentSnap = await getDoc(currentRef);
        if (currentSnap.exists()) {
          const d = currentSnap.data() || {};
          return {
            id: currentId,
            campus: c,
            schoolYear: String(d.schoolYear || ""),
            data: {
              ...d,
              advisories: normalizeAdvisories(d.advisories),
              classes: normalizeClasses(d.classes),
              _meta: { source: "current", ...metaBase }
            }
          };
        }
      } catch (e) {
        metaBase.errors.push({ step: "getCurrent", id: currentId, code: String(e?.code || ""), message: String(e?.message || "") });
      }
    }
    if (uid) {
      try {
        const byUid = await getDocs(query(collection(db, "teacher_workloads_current"), where("uid", "==", uid)));
        const rows = byUid.docs.map(d => ({ id: d.id, ...d.data() }));
        const matches = rows.filter(r => norm(r.campus) === c);
        matches.sort((a, b) => {
          const au = String(a.updatedAt || "");
          const bu = String(b.updatedAt || "");
          if (bu !== au) return bu.localeCompare(au);
          return String(b.schoolYear || "").localeCompare(String(a.schoolYear || ""));
        });
        if (matches.length) {
          const top = matches[0];
          return {
            id: top.id,
            campus: c,
            schoolYear: String(top.schoolYear || ""),
            data: {
              ...top,
              advisories: normalizeAdvisories(top.advisories),
              classes: normalizeClasses(top.classes),
              _meta: { source: "queryCurrentUid", ...metaBase }
            }
          };
        }
      } catch (e) {
        metaBase.errors.push({ step: "queryCurrentUid", code: String(e?.code || ""), message: String(e?.message || "") });
      }
    }
  } catch {
    void 0;
  }
  const syFromSettings = await getActiveSchoolYear(c);
  const sy = schoolYear || syFromSettings || "";
  if (sy) {
    const id = teacherWorkloadDocId({ uid: user?.uid, email: user?.email, campus: c, schoolYear: sy });
    metaBase.attempted.docId = id;
    try {
      const ref = doc(db, "teacher_workloads", id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const d = snap.data() || {};
        return {
          id,
          campus: c,
          schoolYear: sy,
          data: {
            ...d,
            campus: d.campus || c,
            schoolYear: d.schoolYear || sy,
            advisories: normalizeAdvisories(d.advisories),
            classes: normalizeClasses(d.classes),
            _meta: { source: "docId", ...metaBase }
          }
        };
      }
    } catch (e) {
      metaBase.errors.push({ step: "getDocId", id, code: String(e?.code || ""), message: String(e?.message || "") });
    }
  }

  try {
    const uid = resolvedUid || String(user?.uid || "");
    const email = resolvedEmail || String(user?.email || "");
    let byUid = null;
    let byEmail = null;
    if (uid) {
      byUid = await getDocs(query(collection(db, "teacher_workloads"), where("uid", "==", uid)));
    }
    if (email) {
      byEmail = await getDocs(query(collection(db, "teacher_workloads"), where("email", "==", email)));
    }
    const docs = [
      ...(byUid ? byUid.docs.map(d => ({ id: d.id, ...d.data() })) : []),
      ...(byEmail ? byEmail.docs.map(d => ({ id: d.id, ...d.data() })) : [])
    ];
    const uniq = new Map();
    docs.forEach(d => uniq.set(String(d.id), d));
    const merged = Array.from(uniq.values());
    const matches = merged.filter(d => norm(d.campus) === c);
    matches.sort((a, b) => String(b.schoolYear || "").localeCompare(String(a.schoolYear || "")));
    if (matches.length) {
      const top = matches[0];
      return {
        id: top.id,
        campus: c,
        schoolYear: String(top.schoolYear || ""),
        data: {
          ...top,
          advisories: normalizeAdvisories(top.advisories),
          classes: normalizeClasses(top.classes),
          _meta: { source: byUid && byUid.size ? "queryUid" : "queryEmail", ...metaBase }
        }
      };
    }
  } catch {
    void 0;
  }

  let advisories = [];
  try {
    const es = await getDocs(collection(db, "employees"));
    const rows = es.docs.map(d => ({ id: d.id, ...d.data() }));
    const me = rows.find(e => norm(e.email) === norm(user?.email));
    const candidateKeys = [
      String(me?.name || "").trim(),
      String(user?.displayName || "").trim(),
      String(user?.email || "").trim()
    ].filter(Boolean);
    if (candidateKeys.length) {
      const adv = await loadSectionAdvisers(c);
      const list = [];
      Object.keys(adv || {}).forEach(g => {
        const secObj = adv[g] || {};
        Object.keys(secObj).forEach(s => {
          const v = String(secObj[s] || "").trim();
          if (candidateKeys.some(k => norm(k) === norm(v))) list.push({ grade: g, section: s });
        });
      });
      advisories = list;
    }
  } catch {
    advisories = [];
  }

  return {
    id: teacherWorkloadDocId({ uid: user?.uid, email: user?.email, campus: c, schoolYear: sy || "unknown" }),
    campus: c,
    schoolYear: sy,
    data: {
      uid: user?.uid || "",
      email: user?.email || "",
      campus: c,
      schoolYear: sy,
      advisories,
      classes: [],
      _meta: { source: "fallbackAdviserMap", ...metaBase }
    }
  };
}

export async function saveTeacherWorkload({ uid, email, campus, schoolYear, payload }) {
  const id = teacherWorkloadDocId({ uid, email, campus, schoolYear });
  const ref = doc(db, "teacher_workloads", id);
  await setDoc(ref, payload, { merge: true });
  return id;
}

export async function listTeacherUsers() {
  const snap = await getDocs(collection(db, "users"));
  const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  const teachers = rows.filter(u => {
    const r = norm(u.role);
    const rs = Array.isArray(u.roles) ? u.roles.map(norm) : [];
    return r === "teacher" || rs.includes("teacher");
  });
  teachers.sort((a, b) => norm(a.email).localeCompare(norm(b.email)));
  return teachers;
}
